"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = BiometricEnrollCard;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const Theme_1 = require("../Theme");
const Helper_1 = require("../Helper");
const Button_1 = __importDefault(require("../components/Button"));
const CardTitle_1 = __importDefault(require("../components/CardTitle"));
const Card_1 = __importDefault(require("../components/Card"));
const react_native_paper_1 = require("react-native-paper");
const react_native_biometrics_1 = __importDefault(require("react-native-biometrics"));
const async_storage_1 = __importDefault(require("@react-native-async-storage/async-storage"));
const react_i18next_1 = require("react-i18next");
const cancelable_promise_1 = require("cancelable-promise");
const Biometrics_1 = __importDefault(require("../api/Biometrics"));
const UserContext_1 = require("../contexts/UserContext");
const Loading_1 = __importDefault(require("../components/Loading"));
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const ThemeContext_1 = require("../ThemeContext");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const FadeInView_1 = __importDefault(require("../components/FadeInView"));
function BiometricEnrollCard(props) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const promise = react_1.default.useRef(null);
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const [status, setStatus] = react_1.default.useState('done');
    const [biometryTypeAvail, setBiometryTypeAvail] = react_1.default.useState(undefined);
    const [biometricsEnabled, setBiometricsEnabled] = react_1.default.useState(null);
    const [username, setUsername] = react_1.default.useState(null);
    const [prismId, setPrismId] = react_1.default.useState(null);
    const [hasKeys, setHasKeys] = react_1.default.useState(false);
    // Allows passcode to be used as biometric fallback
    const rnBiometrics = new react_native_biometrics_1.default({ allowDeviceCredentials: true });
    const initBiometrics = async () => {
        // Get the user's choice for enabling biometrics and make sure their username and prismId are stored
        let [[, bioSetting], [, uname], [, pId]] = await async_storage_1.default.multiGet(['biometricsEnabled', 'username', 'prismId']);
        setBiometricsEnabled(bioSetting);
        setUsername(uname?.toLowerCase());
        setPrismId(pId);
        let { available, biometryType } = await rnBiometrics.isSensorAvailable();
        // Make sure biometric sensors are available
        if (available && biometryType) {
            setBiometryTypeAvail(biometryType);
            // Ensure the user has biometric keys on device
            let { keysExist } = await rnBiometrics.biometricKeysExist();
            setHasKeys(keysExist);
        }
    };
    const handleDisable = async () => {
        const timestamp = Math.round(new Date().getTime() / 1000).toString();
        const payload = `${username}_${timestamp}`;
        const message = t('profile:text.bioDisable');
        const { success } = await rnBiometrics.simplePrompt({ promptMessage: message });
        if (success) {
            promise.current?.cancel();
            setStatus('loading');
            promise.current = (0, cancelable_promise_1.cancelable)(async_storage_1.default.setItem('biometricsEnabled', 'No').then(() => rnBiometrics.deleteKeys().then(() => 
            // Delete the public key from the user's entity in Firestore
            userContext.getToken().then((t) => Biometrics_1.default.deletePublicKey(t)
                .then(() => {
                setBiometricsEnabled(false);
                setStatus('done');
            })
                .catch(() => {
                setStatus('error');
            })))));
        }
    };
    const handleEnable = async () => {
        const timestamp = Math.round(new Date().getTime() / 1000).toString();
        const payload = `${username}_${timestamp}`;
        const message = t('profile:text.bioEnable');
        // Create biometric key pair and validate biometrics on-device
        const publicKey = await rnBiometrics.createKeys();
        const { success, signature } = await rnBiometrics.createSignature({ promptMessage: message, payload: payload });
        if (success) {
            promise.current?.cancel();
            setStatus('loading');
            promise.current = (0, cancelable_promise_1.cancelable)(async_storage_1.default.setItem('biometricsEnabled', 'Yes').then(() => 
            // Save the public key on the user's entity in Firestore
            userContext.getToken().then((t) => Biometrics_1.default.storePublicKey(t, publicKey).then(() => 
            // Save username and prismId in local storage for use in future biometric auth
            async_storage_1.default.multiSet([
                ['username', userContext.user?.claims?.email?.toLowerCase()],
                ['prismId', userContext.user?.claims?.employee],
            ])))))
                .then(() => {
                setBiometricsEnabled(true);
                setStatus('done');
            })
                .catch(() => {
                setStatus('error');
            });
        }
    };
    react_1.default.useEffect(() => {
        if (!Helper_1.Helper.isPlatformWeb) {
            initBiometrics();
        }
        return () => promise.current?.cancel();
    }, [biometricsEnabled]);
    return ((biometryTypeAvail && ((0, jsx_runtime_1.jsxs)(Card_1.default, { children: [(0, jsx_runtime_1.jsx)(CardTitle_1.default, { title: t('profile:text.bioAuth') }), (status == 'done' && ((0, jsx_runtime_1.jsxs)(FadeInView_1.default, { children: [(0, jsx_runtime_1.jsx)(react_native_paper_1.Text, { style: { marginTop: 8, marginBottom: 12, color: THEME.Text }, children: t('profile:text.bioAuthWhy2') }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { children: [biometricsEnabled == 'Yes' && username && prismId && hasKeys && ((0, jsx_runtime_1.jsxs)(Button_1.default, { type: 'outline', onPress: () => handleDisable(), children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: THEME.Primary, icon: Theme_1.ICONS.Regular.Fingerprint, size: size != 'sm' ? 16 : 14 }), (0, jsx_runtime_1.jsx)(react_native_paper_1.Text, { style: { color: THEME.Primary, fontSize: (size != 'sm' ? 16 : 14), fontWeight: '500', marginLeft: 6 }, children: t('common:buttons.disable') })] })), (biometricsEnabled != 'Yes' || !username || !prismId || !hasKeys) && ((0, jsx_runtime_1.jsxs)(Button_1.default, { type: 'outline', onPress: () => handleEnable(), children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { color: THEME.Primary, icon: Theme_1.ICONS.Regular.Fingerprint, size: size != 'sm' ? 16 : 14 }), (0, jsx_runtime_1.jsx)(react_native_paper_1.Text, { style: { color: THEME.Primary, fontSize: (size != 'sm' ? 16 : 14), fontWeight: '500', marginLeft: 6 }, children: t('common:buttons.enable') })] }))] })] }))) || (0, jsx_runtime_1.jsx)(Loading_1.default, { retryable: true, loading: status == 'loading', error: status == 'error', onRetry: () => handleEnable() })] }))) || (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {}));
}
